import { css, html } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { styleMap } from "lit/directives/style-map.js";
import { html as oghtml, utils } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { Command, CommandGroupSource, CommandQuery } from "@omegagrid/commands";
import rp, { CommandProvider, RequestParams, formatDateTime } from "@reporting/rp-app-host";
import { Ticket } from "@reporting/rp-types/helpdesk";
import { REQUEST_STATUSES, REQUEST_TYPES } from "./types";

export type SearchResultCommand = Command & {
	props: {
		preview?: string;
	};
}

function highlightText(text: string, term: string): string {
	const m = (new RegExp(utils.escapeRegExp(term), 'gi'));
	if (m == null) return null;
	const matches: [number, number][] = [];
	let match: RegExpExecArray;
	while (match = m.exec(text)) {
		matches.push([match.index, match.index + term.length]);
	}
	return oghtml.highlightText(text, matches);
}

export class HelpdeskSearchProvider implements CommandProvider {

	id = 'helpdesk';
	title = msg('Helpdesk');
	placeholder = msg('Search Helpdesk requests...');
	triggerCommand = 'h';
	lastQuery: CommandQuery;

	switches = [
		{key: 'includeClosed', value: msg('Include closed'), checked: false}
	];

	getGroups = async (query?: CommandQuery): Promise<CommandGroupSource[]> => {
		this.lastQuery = query;
		if (!query?.filter) return [];

		const params: RequestParams = {
			fields: ['id_request', 'accounting', 'subject', 'status', 'id_type', 'date_change_status', 'who_change_status_is_sa'],
			order: ['subject'],
			where: `subject='~${query.filter}~'${query.switches?.includes('includeClosed') ? '' : ' AND status<>6'}`
		};

		const tickets = await rp.ms('helpdesk').get<Ticket[]>(`tickets`, params );
		if (tickets.length === 0) return [];

		const group: CommandGroupSource = {id: 'default', name: msg('Requests'), commands: []};
		const groups: CommandGroupSource[] = [group];

		for (const ticket of tickets) {
			group.commands.push({
				id: `helpdesk-ticket-${ticket.id_request}`,
				name: ticket.subject,
				group: group.id,
				props: { ticket },
				exec: (c) => {
					rp.host.open(c.id, {title: c.name});
				}
			});
		}

		return groups;
	}

	getOptions = () => ({
		commandRenderer: (_div: HTMLDivElement, command: SearchResultCommand) => {
			const ticket = command.props?.ticket as Ticket;
			const nameHTML = command.name && this.lastQuery.filter 
				? highlightText(command.name, this.lastQuery.filter) 
				: command.name;

			const detailHTML = ticket ? `
				${msg('Type')}: ${msg(REQUEST_TYPES.find(val => val.k === ticket.id_type)?.v)}<br>
				${msg('Status')}: ${msg(REQUEST_STATUSES.find(val => val.k === ticket.status)?.v)}<br>
				${msg('Last answer')}: 
					${ticket.date_change_status ? formatDateTime(ticket.date_change_status) : '-'} 
					${ticket.who_change_status_is_sa === 1 ? '(RP)' : ''}<br>
			` : null;


			return html`
				<div class="command" style="${styleMap({
					height: detailHTML ? null : '30px',
				})}">
					<div class="command-text">${unsafeHTML(nameHTML)}</div>
					${detailHTML ? html`<div class="command-detail">${unsafeHTML(detailHTML)}</div>` : null}
				</div>
			`;
		},
		itemHeight: 60,
		itemStyle: css`
			.item .command {
				display: flex;
				flex-direction: column;
				justify-content: center;
				height: 60px;
			}

			.item .command {
				flex: 0;
			}

			.command-text {
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}

			.item .command .command-detail {
				margin-left: 20px;
				margin-top: 2px;
				flex: 0 0 40px;
				font-size: 0.8em;
				overflow: hidden;
				text-overflow: ellipsis;
			}
		`
	});
	
}
